import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { usePreviewConfig, useRefreshAggregatedRule, useUpdateConfig } from '@app/sdk';
import { Segment } from 'semantic-ui-react';
import { ConfigEditor } from './ConfigEditor';
import { UpdateConfigSteps } from './UpdateConfigSteps';
import { OuSelector } from './OuSelector';
import { CategorySelector } from './CategorySelector';
import { PreViewChanges } from './PreViewChanges';
import { editor } from 'monaco-editor';
import { toast } from 'react-toastify';
import { Loading } from '../components/LoadingWrapper';
import { ContentHeader } from '@app/shared';
import { Merchant, Product } from '@app/models';
import { AffectedProducts } from '../addConfig/AffectedProducts';
import { AffectedMerchants } from '../addConfig/AffectedMerchants';

export function UpdateConfig({
  enableEditConfig,
  item,
  type,
}: {
  enableEditConfig: Dispatch<SetStateAction<boolean>>;
  item: Product | Merchant;
  type: 'product' | 'merchant';
}) {
  const [step, setStep] = useState(1);
  const [editor, setEditor] = useState<editor.IStandaloneCodeEditor>();
  const [editorContent, setEditorContent] = useState('');
  const [selectedOuId, setSelectedOuId] = useState<string>();
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<any>('');
  const [newConfig, setNewConfig] = useState([]);
  const [request, setRequest] = useState<any>();

  useEffect(() => {
    if (item) {
      setSelectedOuId(item.organisationUnitId);
    }
  }, [item]);

  const cancel = () => {
    enableEditConfig(false);
  };

  useEffect(() => {
    setSelectedCategoryId('');
    setNewConfig([]);
    setEditor(undefined);
    setStep(1);
    setEditorContent(undefined);
  }, [selectedOuId]);

  useEffect(() => {
    setEditorContent(undefined);
  }, [selectedCategoryId]);

  const { mutateAsync: previewConfig, isPending: isPreviewConfigPending } = usePreviewConfig();

  const preview = useCallback(async () => {
    const value = editor?.getValue();
    let req = {} as any;
    try {
      req = JSON.parse(value);
      setRequest(req);
    } catch (e) {
      toast.error('Invalid JSON');
      return false;
    }

    setEditorContent(value);

    const config = await previewConfig({
      request: req,
      categoryId: selectedCategoryId,
      organisationUnitId: selectedOuId,
    });

    setNewConfig(config as any[]);
    return true;
  }, [editor, previewConfig, selectedCategoryId, selectedOuId]);

  const renderSteps = useCallback(
    (step: number) => {
      switch (step) {
        case 1:
          return type === 'product' ? (
            <AffectedProducts selectedOuId={selectedOuId} />
          ) : (
            <AffectedMerchants selectedOuId={selectedOuId} />
          );
        case 2:
          return (
            <ConfigEditor
              selectedOuId={selectedOuId}
              key={selectedOuId + selectedCategoryId}
              categoryId={selectedCategoryId}
              setEditor={setEditor}
              editor={editor}
              editorContent={editorContent}
            />
          );
        case 3:
          return (
            <PreViewChanges
              selectedOuId={selectedOuId}
              type={type}
              selectedCategoryId={selectedCategoryId}
              newConfig={newConfig}
              categories={categories}
            />
          );

        default:
          return <></>;
      }
    },
    [selectedOuId, selectedCategoryId, editor, editorContent, type, newConfig, categories]
  );

  const { mutateAsync: updateConfig, isPending: isUpdateConfigPending } = useUpdateConfig();
  const updateConfigCallBack = useCallback(async () => {
    return await updateConfig({
      request: request,
      categoryId: selectedCategoryId,
      organisationUnitId: selectedOuId,
    });
  }, [request, selectedCategoryId, selectedOuId, updateConfig]);

  const refreshAggregatedRule = useRefreshAggregatedRule();

  const next = async () => {
    switch (step) {
      case 1:
        setStep((step) => step + 1);
        break;
      case 2:
        if (await preview()) {
          setStep((step) => step + 1);
        }
        break;
      case 3:
        await updateConfigCallBack();
        setTimeout(() => {
          refreshAggregatedRule();
        }, 50);
        enableEditConfig(false);
        break;
      default:
        break;
    }
  };

  const previous = () => {
    if (step === 2) {
      setEditorContent(editor?.getValue());
    }
    setStep((step) => step - 1);
  };

  const isPending = isPreviewConfigPending || isUpdateConfigPending;

  return (
    <div>
      <ContentHeader>
        Edit Config
        <button className={'ui button primary'} onClick={() => cancel()}>
          Cancel
        </button>
      </ContentHeader>
      <div className={'ui form'}>
        <OuSelector item={item} selectedOuId={selectedOuId} setSelectedOuId={setSelectedOuId} />
        <CategorySelector
          item={item}
          type={type}
          selectedCategory={selectedCategoryId}
          selectedOuId={selectedOuId}
          setSelectedCategory={(categoryId: string) => {
            setSelectedCategoryId(categoryId);
          }}
          categories={categories}
          setCategories={setCategories}
        />
        {selectedCategoryId && (
          <>
            <UpdateConfigSteps step={step} type={type} />
            {renderSteps(step)}

            <div className={'fields'} style={{ paddingTop: '5px' }}>
              <button className={'ui button primary'} onClick={() => previous()} disabled={step === 1}>
                Back
              </button>

              <button className={'ui button primary'} onClick={() => next()}>
                {step <= 2 ? 'Next' : 'Confirm Update Config'}
              </button>
              <Loading loading={isPending} />
            </div>
          </>
        )}

        {!selectedCategoryId && (
          <Segment style={{ height: '450px' }}>
            <ContentHeader>Select a category to start </ContentHeader>
            If no categories are available, it is because there is no config on the selected organisation unit to
            update.
          </Segment>
        )}
      </div>
    </div>
  );
}
